/**
 * Shared payload utilities used by both src/app/features/ (API layer) and
 * src/pages/activityBuilder/ (UI layer).
 *
 * Extracted here to break the inverted dependency where app/features/ was
 * importing from src/pages/. See docs/adr/0001-feature-vs-page-layering.md.
 */
import { getIn } from "formik";

// ─── Formik error / touched helpers ─────────────────────────────────────────

export interface Errors {
	[key: string]: any;
}

export interface Touched {
	[key: string]: any;
}

export const checkErrorsTouched = (errors: Errors, touched: Touched): boolean => {
	const checkNestedErrors = (nestedErrors: any, nestedTouched: any): boolean => {
		for (const key in nestedErrors) {
			const errorValue = nestedErrors[key];
			const touchedValue = getIn(nestedTouched, key);

			if (Array.isArray(errorValue)) {
				for (let i = 0; i < errorValue.length; i++) {
					if (checkNestedErrors(errorValue[i], getIn(nestedTouched, `${key}[${i}]`))) {
						return true;
					}
				}
			} else if (typeof errorValue === "object" && errorValue !== null) {
				if (checkNestedErrors(errorValue, touchedValue)) return true;
			} else {
				if (touchedValue === true) return true;
			}
		}
		return false;
	};

	return checkNestedErrors(errors, touched);
};

// ─── Object / payload utilities ──────────────────────────────────────────────

export const filterObjectProps = (
	rawObj: { [key: string]: any },
	props: string[],
) =>
	Object.keys(rawObj)
		.filter((key) => props.includes(key))
		.reduce((obj, key) => {
			return { ...obj, [key]: rawObj[key] };
		}, {});

export const convertIncomingPercentage = (obj: any, props: string[]) => {
	for (const key in obj) {
		const hasToBeConverted =
			props.some(
				(prop) =>
					key.startsWith(prop) &&
					["start", "w", "wo", "start_default", "w_default", "wo_default"]
						.map((status) => key.endsWith(status))
						.some((status) => status),
			) && obj[key];

		if (key !== "id" && hasToBeConverted && obj[key] !== null)
			obj[key] = +(obj[key] * 100).toFixed(5);
	}
};

export const convertOutgoingPercentage = (obj: any, props: string[]) => {
	for (const key in obj) {
		const hasToBeConverted =
			props.some(
				(prop) =>
					key.startsWith(prop) &&
					["start", "w", "wo", "start_default", "w_default", "wo_default"]
						.map((status) => key.endsWith(status))
						.some((status) => status),
			) && obj[key];

		if (key !== "id" && hasToBeConverted && obj[key] !== null)
			obj[key] = obj[key] / 100;
	}
};

type MutateBody = Record<string, unknown>;
export const convertEmptyStringsToNull: (obj: MutateBody) => MutateBody = (obj) => {
	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			if (obj[key] === "") {
				obj[key] = null;
			}
		}
	}
	return obj;
};

// ─── Thread ID extraction ────────────────────────────────────────────────────

export function getThreadIds<T>(modules: Partial<T>) {
	const threadIds: number[] = [];
	for (const moduleKey in modules) {
		const moduleSection = modules[moduleKey];
		if (Array.isArray(moduleSection)) {
			for (const repeatable of moduleSection) {
				getThreadFieldFromSection(repeatable);
			}
		} else if (moduleSection) {
			getThreadFieldFromSection(moduleSection);
		}
	}
	return threadIds;

	function getThreadFieldFromSection(section: Record<string, unknown>) {
		for (const key in section) {
			const value = section[key];
			if (key.includes("thread") && value) {
				threadIds.push(value as number);
			}
		}
	}
}
